/**
 * src/websocket.js
 * Centralized WebSocket handler for real-time updates in the voipbin project.
 * Supports dynamic URL injection, automatic reconnection, and Redux integration.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getWebSocketUrl } from "./config/hostname";
import {
    AImessagesAddWithAIcallID,
    AImessagesAppendIntermediateWithAIcallID,
    ConversationmessagesAddWithConversationID,
    WebchatmessagesAddWithSessionID,
    WebchatsessionEndedWithSessionID,
    selectEffectiveToken,
    selectEffectiveAgentInfo,
} from "./store";
import { captureAicallId } from "./lib/testCall";

// --- Module-level dynamic subscription state ---
// Reference-counted map of topic -> subscriber count
const dynamicTopics = new Map();
// Reference to the live WebSocket instance (set by WebSocketHandler)
let wsRef = null;

function sendSubscribe(topics) {
    if (wsRef && wsRef.readyState === WebSocket.OPEN) {
        wsRef.send(JSON.stringify({ type: "subscribe", topics }));
    }
}

function sendUnsubscribe(topics) {
    if (wsRef && wsRef.readyState === WebSocket.OPEN) {
        wsRef.send(JSON.stringify({ type: "unsubscribe", topics }));
    }
}

/**
 * resolveEventType(payload) — determines which of the 5 known event kinds
 * a WS frame is, purely by its SHAPE (design doc, 2026-07-19
 * websocket-js-envelope-mismatch). The server's zmqSub->WS relay pipeline
 * (bin-api-manager's zmqsubhandler.RunWithMutex) writes the resource
 * payload directly with NO {type, data} envelope, so this cannot rely on
 * a `type` field -- there isn't one. The discriminator keys below mirror
 * each resource's actual Go WebhookMessage struct field set.
 *
 * IMPORTANT: existence checks use `!== undefined`, never truthiness.
 * `sequence` has no `omitempty` tag on the server (bin-ai-manager's
 * IntermediateWebhookMessage) and can legitimately be 0 on the first
 * delta chunk -- `if (payload.sequence)` would misclassify that as
 * aimessage_created (same bug class PR #362's client.test.js hit).
 *
 * Order matters: webchat_message_created and webchat_session_ended both
 * carry `widget_id`, so `session_id` (present only on the message shape)
 * MUST be checked before falling back to the session-ended case.
 *
 * @returns {'aimessage_created'|'aimessage_intermediate'|'conversation_message_created'|'webchat_message_created'|'webchat_session_ended'|null}
 *   One of the 5 known event type strings, or null if the payload matches
 *   none of them (falls through to the existing `default` console.warn
 *   branch in handleMessage below).
 */
export function resolveEventType(payload) {
    if (payload === null || typeof payload !== "object") return null;

    if (payload.aicall_id !== undefined) {
        return payload.sequence !== undefined ? "aimessage_intermediate" : "aimessage_created";
    }
    if (payload.conversation_id !== undefined) {
        return "conversation_message_created";
    }
    if (payload.session_id !== undefined) {
        return "webchat_message_created";
    }
    if (payload.widget_id !== undefined) {
        return "webchat_session_ended";
    }
    return null;
}

/**
 * Subscribe to a dynamic topic (ref-counted).
 * Safe to call multiple times — only the first call sends the WS message.
 */
export function subscribeTopic(topic) {
    const count = dynamicTopics.get(topic) || 0;
    dynamicTopics.set(topic, count + 1);
    if (count === 0) {
        console.log("WS: Dynamic subscribe:", topic);
        sendSubscribe([topic]);
    }
}

/**
 * Unsubscribe from a dynamic topic (ref-counted).
 * Only sends the WS unsubscribe when count reaches 0.
 */
export function unsubscribeTopic(topic) {
    const count = dynamicTopics.get(topic) || 0;
    if (count <= 1) {
        dynamicTopics.delete(topic);
        console.log("WS: Dynamic unsubscribe:", topic);
        sendUnsubscribe([topic]);
    } else {
        dynamicTopics.set(topic, count - 1);
    }
}

const WebSocketHandler = () => {
    const ws = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [shouldReconnect, setShouldReconnect] = useState(false);
    
    const dispatch = useDispatch();
    
    // Extract authentication state from Redux store
    const authTokenFromRedux = useSelector(selectEffectiveToken);
    const agentInfo = useSelector(selectEffectiveAgentInfo);

    const maxRetries = 10;

    // WebSocket URL derived from base hostname configuration
    const websocketUrl = getWebSocketUrl();

    /**
     * Define subscription topics based on the logged-in agent's information.
     */
    const topics = useMemo(() => {
        if (!agentInfo?.customer_id) {
            return [];
        }
        return [
            `customer_id:${agentInfo.customer_id}:call`,
            `customer_id:${agentInfo.customer_id}:activeflow`,
            `customer_id:${agentInfo.customer_id}:aicall`,
            `customer_id:${agentInfo.customer_id}:conversation`
        ];
    }, [agentInfo]);

    /**
     * Handle incoming messages and dispatch actions to the Redux store.
     */
    const handleMessage = useCallback((event) => {
        try {
            const parsedMessage = JSON.parse(event.data);
            const resolvedType = resolveEventType(parsedMessage);
            console.log("WS: Message received:", resolvedType);

            switch (resolvedType) {
                case "aimessage_created":
                    dispatch(AImessagesAddWithAIcallID(parsedMessage.aicall_id, parsedMessage));
                    captureAicallId(parsedMessage.aicall_id);
                    break;
                case "aimessage_intermediate":
                    dispatch(AImessagesAppendIntermediateWithAIcallID(parsedMessage.aicall_id, parsedMessage));
                    captureAicallId(parsedMessage.aicall_id);
                    break;
                case "conversation_message_created":
                    dispatch(ConversationmessagesAddWithConversationID(parsedMessage.conversation_id, parsedMessage));
                    break;
                case "webchat_message_created":
                    dispatch(WebchatmessagesAddWithSessionID(parsedMessage.session_id, parsedMessage));
                    break;
                case "webchat_session_ended":
                    dispatch(WebchatsessionEndedWithSessionID(parsedMessage.id || parsedMessage.session_id));
                    break;
                default:
                    console.warn("WS: Unhandled/unrecognized message shape:", parsedMessage);
                    break;
            }
        } catch (error) {
            console.error("WS: Error parsing incoming message:", error);
        }
    }, [dispatch]);

    /**
     * Cleanly disconnect the current WebSocket instance.
     *
     * `clearDynamicTopics` defaults to true (real teardown: logout, unmount).
     * connectWebSocket passes false for its internal "Resetting connection"
     * reconnect path, so a reconnect doesn't wipe dynamicTopics before
     * onopen has a chance to re-subscribe them (SQUARE-64).
     */
    const disconnectWebSocket = useCallback((reason = "Unspecified", clearDynamicTopics = true) => {
        setShouldReconnect(false);

        if (ws.current) {
            console.log(`WS: Disconnecting. Reason: ${reason}`);
            ws.current.onopen = null;
            ws.current.onmessage = null;
            ws.current.onerror = null;
            ws.current.onclose = null;

            if (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) {
                ws.current.close(1000, reason);
            }
            ws.current = null;
            wsRef = null;
            if (clearDynamicTopics) {
                dynamicTopics.clear();
            }
            setIsConnected(false);
        }
    }, []);

    /**
     * Establish a new WebSocket connection.
     */
    const connectWebSocket = useCallback(() => {
        // Pre-connection validation
        if (!authTokenFromRedux || !agentInfo?.customer_id) {
            console.warn("WS: Connection aborted - Required auth/agent info is missing.");
            return;
        }

        if (ws.current) {
            // Keep dynamicTopics intact across a reconnect so onopen can
            // re-subscribe them below (SQUARE-64) — only a real
            // logout/unmount teardown (disconnectWebSocket's default) clears it.
            disconnectWebSocket("Resetting connection", false);
        }

        const urlWithAuth = `${websocketUrl}?token=${authTokenFromRedux}`;

        try {
            const localWsInstance = new WebSocket(urlWithAuth);
            ws.current = localWsInstance;
            wsRef = localWsInstance;

            localWsInstance.onopen = () => {
                console.log("WS: >>> Connection established successfully <<<");
                setIsConnected(true);
                setRetryCount(0);
                setShouldReconnect(false);

                if (topics.length > 0) {
                    console.log("WS: Subscribing to topics:", topics);
                    localWsInstance.send(JSON.stringify({ type: "subscribe", topics }));
                }

                // Re-subscribe to any active dynamic topics
                const activeDynamic = Array.from(dynamicTopics.keys());
                if (activeDynamic.length > 0) {
                    console.log("WS: Re-subscribing dynamic topics:", activeDynamic);
                    localWsInstance.send(JSON.stringify({ type: "subscribe", topics: activeDynamic }));
                }
            };

            localWsInstance.onmessage = handleMessage;

            localWsInstance.onclose = (event) => {
                console.log(`WS: Connection closed. Code: ${event.code}, Reason: ${event.reason}`);
                setIsConnected(false);
                
                // Trigger reconnection for non-standard closures
                if (authTokenFromRedux && event.code !== 1000) {
                    setShouldReconnect(true);
                }
            };

            localWsInstance.onerror = (err) => {
                console.error("WS: WebSocket error detected:", err);
                setIsConnected(false);
                setShouldReconnect(true);
            };

        } catch (error) {
            console.error("WS: Critical failure during initialization:", error);
            setShouldReconnect(true);
        }
    }, [websocketUrl, authTokenFromRedux, agentInfo, topics, handleMessage, disconnectWebSocket]);

    /**
     * Effect: Manage connection lifecycle based on authentication status.
     */
    useEffect(() => {
        if (authTokenFromRedux && agentInfo?.customer_id) {
            connectWebSocket();
        } else {
            disconnectWebSocket("Missing required session info");
        }

        return () => disconnectWebSocket("Cleaning up WebSocket component");
    }, [authTokenFromRedux, agentInfo, connectWebSocket, disconnectWebSocket]);

    /**
     * Effect: Handle automatic reconnection with exponential backoff.
     */
    useEffect(() => {
        let timerId;
        if (shouldReconnect && authTokenFromRedux && retryCount < maxRetries) {
            const delay = 3000 * Math.pow(2, Math.min(retryCount, 3));
            console.log(`WS: Reconnect attempt ${retryCount + 1}/${maxRetries} in ${delay / 1000}s`);
            
            timerId = setTimeout(() => {
                setRetryCount(prev => prev + 1);
                setShouldReconnect(false);
                connectWebSocket();
            }, delay);
        }
        return () => clearTimeout(timerId);
    }, [shouldReconnect, retryCount, authTokenFromRedux, connectWebSocket]);

    return <div style={{ display: 'none' }} data-testid="websocket-handler" />;
};

export default WebSocketHandler;